import React, { useState } from 'react';
import { BusinessOffer } from '../../../types/graphql';
import OfferCard from './OfferCard';
import AcceptOfferForm from './AcceptOfferForm';
import { X } from 'lucide-react';
import { gql, useQuery } from '@apollo/client';
import { GET_BUSINESS_OFFERS } from '../../../graphql/admin/queries/business';

interface OffersListProps {
  offers: BusinessOffer[];
  onRefetch: () => void;
  onOfferAccepted?: () => void;
  onAddTravelers?: (offerId: number) => void;
  onDeleteOffer: (offerId: number) => void;
  onEditOffer?: (offerId: number) => void;
  businessId: number;
}

export default function OffersList({ 
  offers, 
  onRefetch, 
  onOfferAccepted,
  onAddTravelers,
  onDeleteOffer,
  onEditOffer,
  businessId
}: OffersListProps) {
  // État pour l'offre sélectionnée pour acceptation
  const [selectedOfferForAccept, setSelectedOfferForAccept] = useState<BusinessOffer | null>(null);
  const [showAcceptForm, setShowAcceptForm] = useState(false);

  // Fonction pour gérer le succès de l'acceptation
  const handleAcceptSuccess = () => {
    setShowAcceptForm(false);
    setSelectedOfferForAccept(null);
    
    // Appeler la fonction de rappel si elle existe
    if (onOfferAccepted) {
      onOfferAccepted();
    } else {
      // Sinon, simplement rafraîchir les données
      onRefetch();
    }
  };

  const handleDeleteOffer = async (offerId: number) => {
    try {
      
      // Vérifier si l'offre a des réservations
      const offer = offers.find(o => o.id === offerId);
      if (offer?.bookings && offer.bookings.length > 0) {
        alert('Impossible de supprimer cette offre car elle contient des réservations.');
        return;
      }

      // Appeler directement la fonction de suppression sans confirmation supplémentaire
      await onDeleteOffer(offerId);
      
      // Rafraîchir la liste
      onRefetch();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Une erreur est survenue lors de la suppression de l\'offre.');
    }
  };

  // Utiliser GET_BUSINESS_OFFERS avec le businessId reçu en prop
  const { data, loading, error } = useQuery(GET_BUSINESS_OFFERS, {
    variables: { businessId },
    fetchPolicy: 'network-only'
  });

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">Historique des offres</h2>
      {offers.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          Aucune offre envoyée
        </div>
      ) : (
        <div className="space-y-4">
          {offers.map((offer) => (
            <div key={offer.id} className="bg-white rounded-lg shadow p-6">
              <OfferCard 
                offer={offer} 
                package={offer.package}
                onAddTravelers={onAddTravelers}
                onRefresh={onRefetch}
              />
              
              {offer.status === 'ACCEPTED' ? (
                <div className="mt-4">
                  {offer.bookings && offer.bookings.length > 0 ? (
                    <div className="space-y-4">
                      {/* ... contenu des bookings ... */}
                    </div>
                  ) : (
                    <div className="flex justify-between items-center">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => onEditOffer?.(offer.id)}
                          className="text-sm px-3 py-1 text-blue-600 hover:text-blue-800 border border-blue-600 rounded-md hover:bg-blue-50"
                        >
                          Modifier
                        </button>
                        <button
                          onClick={() => handleDeleteOffer(offer.id)}
                          className="text-sm px-3 py-1 text-red-600 hover:text-red-800 border border-red-600 rounded-md hover:bg-red-50"
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
      
      {showAcceptForm && selectedOfferForAccept && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Accepter l'offre</h3>
              <button
                onClick={() => {
                  setShowAcceptForm(false);
                  setSelectedOfferForAccept(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <AcceptOfferForm
              offer={selectedOfferForAccept}
              onSuccess={handleAcceptSuccess}
              onClose={() => {
                setShowAcceptForm(false);
                setSelectedOfferForAccept(null);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}